import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Heart, ArrowRight, Quote, Users, GraduationCap, Briefcase, UtensilsCrossed, HandHeart } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import Marquee from "react-fast-marquee";
import axios from "axios";
import logger from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const stories = [
  { id: "jaxyn", name: "Jaxyn Young", quote: "My experience with SSYC's Black College Expo field trip was very exciting and informative. I enjoyed talking with college representatives and getting a taste of college." },
  { id: "dasha", name: "Deashanna 'Dasha' Hampton Lewis", quote: "SSYC transformed me by teaching self-improvement, resume building, and real world skills, for personal growth." },
  { id: "tj", name: "T.J. Bean", quote: "SSYC has changed my mindset. I'm not just surviving, I'm building my future." },
  { id: "priscila", name: "Priscila 'P' Salazar", quote: "SOAR is more than a program, it's a home that empowers youth to grow in life, school, work, and daily routines." },
  { id: "enrique", name: "Enrique Magana", quote: "I wrote down goals and actually accomplished them, thanks to SSYC." },
  { id: "masiah", name: "Masiah Jackson", quote: "Thank you so much SSYC for your environment and impact on us as youth!" },
];

const stats = [
  { icon: Users, value: "50+", label: "Youth Served" },
  { icon: GraduationCap, value: "15", label: "To College" },
  { icon: Briefcase, value: "12", label: "Jobs Secured" },
  { icon: UtensilsCrossed, value: "903", label: "Meals Served" },
];

const Home = () => {
  const [events, setEvents] = useState([]);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events`);
      setEvents(res.data.slice(0, 4));
    } catch (e) {
      logger.error("Failed to fetch events:", e);
    }
  }, []);

  useEffect(() => {
    document.title = "Soar Solutions Youth Center | Empowering Youth Through Mentorship";
    fetchEvents();
  }, [fetchEvents]);

  return (
    <div data-testid="home-page" className="bg-white">

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* BG Image */}
        <div className="absolute inset-0">
          <img
            src="https://customer-assets.emergentagent.com/job_98cd7b09-1d25-49bf-8206-01ee668f439d/artifacts/5pr1yg1o_Screenshot%202026-01-23%20090805.png"
            alt="SOAR Youth"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-soar-blue via-soar-blue/90 to-soar-blue/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-3xl space-y-8">
            <div className="inline-block border border-soar-teal/60 rounded-full px-4 py-1.5 text-xs font-semibold tracking-widest text-soar-teal uppercase opacity-0 animate-fade-up" style={{ animationFillMode: 'forwards' }}>
              Take Control of Your Future
            </div>

            <h1 className="font-heading text-7xl sm:text-8xl lg:text-9xl leading-none tracking-tight text-white opacity-0 animate-fade-up" style={{ animationDelay: '100ms', animationFillMode: 'forwards' }}>
              Think.<br />
              <span className="text-soar-gold">Build.</span><br />
              Dream.
            </h1>

            <p className="text-lg text-white/70 max-w-xl leading-relaxed font-light opacity-0 animate-fade-up" style={{ animationDelay: '200ms', animationFillMode: 'forwards' }}>
              We strive to empower and equip youth with the necessary tools to achieve{" "}
              <span className="text-soar-teal font-semibold">Successful Outcomes And Results</span>.
            </p>

            <div className="flex flex-wrap gap-4 opacity-0 animate-fade-up" style={{ animationDelay: '300ms', animationFillMode: 'forwards' }}>
              <Link to="/mentorship-form" data-testid="hero-mentorship-btn">
                <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-soar-blue px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200 shadow-lg">
                  Apply Now <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/donate" data-testid="hero-donate-btn">
                <Button className="rounded-full bg-white/20 hover:bg-white/30 text-white border border-white/30 px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  <Heart className="w-5 h-5 mr-2" /> Donate
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== MARQUEE STRIP ===== */}
      <div className="py-5" style={{ backgroundColor: '#212163' }}>
        <Marquee speed={60} gradient={false} className="overflow-hidden">
          {["EMPOWER", "ELEVATE", "SOAR", "MENTOR", "LEAD", "DREAM", "BUILD", "GROW"].map((word) => (
            <span key={word} className="font-heading text-4xl mx-12 tracking-wider" style={{ color: 'rgba(221,182,77,0.3)' }}>{word}</span>
          ))}
        </Marquee>
      </div>

      {/* ===== IMPACT STATS ===== */}
      <section className="py-16 bg-white" data-testid="impact-stats">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s) => (
              <div key={s.label} className="text-center card-elevated rounded-3xl p-8">
                <s.icon className="w-8 h-8 text-soar-teal mx-auto mb-4" aria-hidden="true" />
                <p className="font-heading text-6xl text-soar-blue tracking-tight">{s.value}</p>
                <p className="text-soar-blue/50 text-sm font-medium mt-2">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MARQUEE STRIP 2 ===== */}
      <div className="py-5 bg-soar-blue">
        <Marquee speed={40} direction="right" gradient={false}>
          {["SOLUTIONS", "OPPORTUNITIES", "ADVOCACY", "RESOURCES", "S.O.A.R."].map((w) => (
            <span key={w} className="font-heading text-4xl text-soar-gold/20 mx-12 tracking-wider">{w}</span>
          ))}
        </Marquee>
      </div>

      {/* ===== MISSION BLOCK ===== */}
      <section className="py-24 pb-12 bg-gradient-to-b from-white to-soar-teal/5" data-testid="mission-section">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-5xl md:text-7xl text-soar-blue tracking-tight mb-8">
            What We <span className="text-soar-gold">Believe</span>
          </h2>
          <p className="text-xl text-soar-blue/60 leading-relaxed mb-8 font-light">
            If we haven&apos;t figured out how to make it easier for you to grow as a person, then we&apos;re
            probably not finished yet. It&apos;s not just about changing your mental but making sure we
            shape your heart.
          </p>
          <p className="font-accent text-3xl text-soar-teal">
            It&apos;s all about whether or not the outcome made a difference in your day to day.
          </p>
        </div>
      </section>

      {/* ===== YOUTH SUCCESS ===== */}
      <section className="pt-12 pb-24 bg-soar-teal/5" data-testid="youth-success-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-soar-teal text-xs font-semibold tracking-widest uppercase">Real Results</span>
            <h2 className="font-heading text-5xl md:text-7xl text-soar-blue tracking-tight mt-2">Youth <span className="text-soar-gold">Success</span></h2>
            <p className="text-soar-blue/50 mt-4 max-w-2xl mx-auto">
              Our youth are achieving their dreams — landing jobs, starting careers, and heading to college.
            </p>
          </div>

          {/* Jobs Secured */}
          <div className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-soar-teal/15 flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-soar-teal" />
              </div>
              <div>
                <h3 className="font-heading text-2xl text-soar-blue tracking-tight">Jobs Secured</h3>
                <p className="text-soar-teal text-sm font-semibold">Building careers, one opportunity at a time</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Sy'ere W. */}
              <div className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-72">
                  <img 
                    src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/3zxnvq71_IMG_7624.JPEG" 
                    alt="Sy'ere W." 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/30 to-transparent" />
                </div>
                <div className="p-5 bg-white">
                  <h4 className="font-heading text-xl text-soar-blue mb-1">Sy&apos;ere W.</h4>
                  <p className="text-soar-teal font-semibold text-sm">Handel&apos;s Homemade Ice Cream</p>
                </div>
              </div>

              {/* Devontay O. - Job */}
              <div className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-72">
                  <img 
                    src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/bwfskg3y_IMG_6061.webp" 
                    alt="Devontay O." 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/30 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="bg-soar-gold text-soar-blue text-[10px] font-bold px-2 py-1 rounded-full uppercase">Also College Bound</span>
                  </div>
                </div>
                <div className="p-5 bg-white">
                  <h4 className="font-heading text-xl text-soar-blue mb-1">Devontay O.</h4>
                  <p className="text-soar-teal font-semibold text-sm">Sky Zone</p>
                </div>
              </div>

              {/* Jaxyn Y. */}
              <div className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-72">
                  <img 
                    src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/xl3c7hxl_IMG_8531%20%281%29.JPEG" 
                    alt="Jaxyn Y." 
                    className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/30 to-transparent" />
                </div>
                <div className="p-5 bg-white">
                  <h4 className="font-heading text-xl text-soar-blue mb-1">Jaxyn Y.</h4>
                  <p className="text-soar-teal font-semibold text-sm">Sky Zone</p>
                </div>
              </div>
            </div>
          </div>

          {/* College Bound */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 rounded-2xl bg-soar-gold/15 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-soar-gold" />
              </div>
              <div>
                <h3 className="font-heading text-2xl text-soar-blue tracking-tight">College Bound</h3>
                <p className="text-soar-gold text-sm font-semibold">Furthering their education, chasing their dreams</p>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Khushwant */}
              <div className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-72">
                  <img 
                    src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/5j2vuyru_IMG_8530.webp" 
                    alt="Khushwant" 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/30 to-transparent" />
                </div>
                <div className="p-5 bg-white">
                  <h4 className="font-heading text-xl text-soar-blue mb-1">Khushwant</h4>
                  <p className="text-soar-gold font-semibold text-sm">UCLA</p>
                </div>
              </div>

              {/* Devontay O. - College */}
              <div className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-72">
                  <img 
                    src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/bwfskg3y_IMG_6061.webp" 
                    alt="Devontay O." 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/30 to-transparent" />
                  <div className="absolute top-4 right-4">
                    <span className="bg-soar-teal text-white text-[10px] font-bold px-2 py-1 rounded-full uppercase">Also Employed</span>
                  </div>
                </div>
                <div className="p-5 bg-white">
                  <h4 className="font-heading text-xl text-soar-blue mb-1">Devontay O.</h4>
                  <p className="text-soar-gold font-semibold text-sm">Bakersfield College</p>
                </div>
              </div>

              {/* Adrianna L. */}
              <div className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-72 overflow-hidden">
                  <img 
                    src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/h8d0c4k5_IMG_7034.webp" 
                    alt="Adrianna L." 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/30 to-transparent" />
                </div>
                <div className="p-5 bg-white">
                  <h4 className="font-heading text-xl text-soar-blue mb-1">Adrianna L.</h4>
                  <p className="text-soar-gold font-semibold text-sm">Bakersfield College</p>
                </div>
              </div>

              {/* Savannah T. */}
              <div className="card-elevated rounded-3xl overflow-hidden group">
                <div className="relative h-72">
                  <img 
                    src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/kg8t71up_IMG_3073.JPEG" 
                    alt="Savannah T." 
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-soar-blue via-soar-blue/30 to-transparent" />
                </div>
                <div className="p-5 bg-white">
                  <h4 className="font-heading text-xl text-soar-blue mb-1">Savannah T.</h4>
                  <p className="text-soar-gold font-semibold text-sm">Bakersfield College</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/programs" data-testid="view-programs-btn">
              <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 text-white px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200 shadow-md">
                Explore Our Programs <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ===== MARQUEE 2 ===== */}
      <div className="py-5 bg-soar-blue">
        <Marquee speed={40} direction="right" gradient={false}>
          {["SOLUTIONS", "OPPORTUNITIES", "ADVOCACY", "RESOURCES", "S.O.A.R."].map((w) => (
            <span key={w} className="font-heading text-4xl text-soar-gold/20 mx-12 tracking-wider">{w}</span>
          ))}
        </Marquee>
      </div>

      {/* ===== STORIES ===== */}
      <section className="py-24 bg-white" data-testid="success-stories">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-14">
            <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">Real Impact</span>
            <h2 className="font-heading text-5xl md:text-7xl text-soar-blue tracking-tight mt-2">Youth Stories</h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {stories.map((s) => (
              <div key={s.id} className="card-elevated rounded-3xl p-8 group">
                <Quote className="w-8 h-8 text-soar-gold/40 mb-5" aria-hidden="true" />
                <p className="text-soar-blue/60 italic leading-relaxed text-sm mb-6">{s.quote}</p>
                <p className="text-soar-blue font-bold text-sm">{s.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SCHOLARSHIP ===== */}
      <section className="py-24 bg-soar-blue" data-testid="scholarship-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">In Loving Memory</span>
              <h2 className="font-heading text-5xl md:text-7xl text-white tracking-tight">
                Keiah White<br />Memorial<br />Scholarship
              </h2>
              <p className="font-accent text-3xl text-soar-gold">&quot;A light that lifted others.&quot;</p>
              <p className="text-white/60 leading-relaxed max-w-xl">
                Supporting high school seniors who demonstrate compassion, leadership,
                and community involvement—values Keiah embodied every day.
              </p>
              <Link to="/scholarship" data-testid="scholarship-cta-btn">
                <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 text-white px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200">
                  Learn More <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="bg-white/10 border border-white/20 rounded-3xl p-10 text-center">
                <GraduationCap className="w-14 h-14 text-soar-gold mx-auto mb-4" aria-hidden="true" />
                <h3 className="font-heading text-3xl text-white">For Seniors</h3>
                <p className="text-white/60 mt-2 text-sm">Compassion, leadership, and community involvement</p>
                <p className="text-white/40 text-xs mt-4">Class of 2025 &middot; Forever in Our Hearts</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== VOLUNTEER CTA ===== */}
      <section className="py-24 bg-gradient-to-b from-white to-soar-teal/5" data-testid="volunteer-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="card-elevated rounded-3xl overflow-hidden">
            <div className="grid lg:grid-cols-2">
              {/* Image Side */}
              <div className="relative h-64 lg:h-auto">
                <img
                  src="https://customer-assets-v7afamib.emergentagent.net/job_124fb946-5a2a-4ff0-97cd-9590e462d55b/artifacts/m87cdu4y_IMG_7738.webp"
                  alt="Volunteers making a difference"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-soar-teal/80 to-soar-teal/40 lg:bg-gradient-to-t" />
                <div className="absolute inset-0 flex items-center justify-center lg:hidden">
                  <HandHeart className="w-20 h-20 text-white/30" />
                </div>
              </div>
              
              {/* Content Side */}
              <div className="p-8 lg:p-12 flex flex-col justify-center">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-2xl bg-soar-teal/15 flex items-center justify-center">
                    <HandHeart className="w-6 h-6 text-soar-teal" />
                  </div>
                  <span className="text-soar-teal text-xs font-semibold tracking-widest uppercase">Make a Difference</span>
                </div>
                
                <h2 className="font-heading text-4xl lg:text-5xl text-soar-blue tracking-tight mb-4">
                  Become a <span className="text-soar-gold">Volunteer</span>
                </h2>
                
                <p className="text-soar-blue/60 leading-relaxed mb-6">
                  Share your time, skills, and passion to help empower the next generation. 
                  Whether you can mentor, teach a workshop, or help at events — every contribution matters.
                </p>
                
                <div className="flex flex-wrap gap-3 mb-8">
                  <span className="px-3 py-1.5 bg-soar-teal/10 text-soar-teal text-xs font-semibold rounded-full">Mentoring</span>
                  <span className="px-3 py-1.5 bg-soar-gold/10 text-soar-gold text-xs font-semibold rounded-full">Event Support</span>
                  <span className="px-3 py-1.5 bg-soar-blue/10 text-soar-blue text-xs font-semibold rounded-full">Workshop Leaders</span>
                  <span className="px-3 py-1.5 bg-soar-teal/10 text-soar-teal text-xs font-semibold rounded-full">Career Coaches</span>
                </div>
                
                <Link to="/volunteer-form" data-testid="volunteer-cta-btn">
                  <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 text-white px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200 shadow-md">
                    Apply to Volunteer <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PARTNER CTA ===== */}
      <section className="py-24 relative overflow-hidden bg-soar-gold" data-testid="partners-cta">
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-5xl md:text-7xl text-soar-blue tracking-tight mb-6">
            How You Can Help
          </h2>
          <p className="text-soar-blue/70 text-lg mb-10 max-w-2xl mx-auto">
            Our job is to teach what has not been taught. Partner with us to empower the next generation.
          </p>
          <Link to="/partners">
            <Button className="rounded-full bg-soar-blue hover:bg-soar-blue/90 text-white px-10 py-6 text-lg font-bold hover:scale-105 transition-transform duration-200 shadow-lg">
              Partner With Us <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-32 bg-gradient-to-b from-white to-soar-teal/5 relative" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-6xl md:text-8xl text-soar-blue tracking-tight mb-6">
            Your Life<br />On Full <span className="text-soar-gold">Blast</span>
          </h2>
          <p className="text-soar-blue/50 text-lg mb-12 max-w-2xl mx-auto">
            Ready to turn the volume up and grow your confidence through effective mentorship?
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/mentorship-form" data-testid="cta-join-btn">
              <Button className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-soar-blue px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200 shadow-md">
                Start with a Call
              </Button>
            </Link>
            <Link to="/referral-form" data-testid="cta-referral-btn">
              <Button className="rounded-full bg-soar-blue/10 border border-soar-blue/20 text-soar-blue px-8 py-6 text-base font-bold hover:bg-soar-blue/15 hover:scale-105 transition-transform duration-200">
                Refer a Youth
              </Button>
            </Link>
            <Link to="/donate" data-testid="cta-donate-btn">
              <Button className="rounded-full bg-soar-teal hover:bg-soar-teal/90 text-white px-8 py-6 text-base font-bold hover:scale-105 transition-transform duration-200 shadow-md">
                <Heart className="w-5 h-5 mr-2" /> Donate Now
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
